// -- Secure channel handshake protocol (DX-8174 / DX-8205) -------------------
// Shared by every Highnote SDK that delivers credentials to a hosted iframe
// without placing them in the iframe URL (browser history/sync, web archives,
// CDN/proxy logs, analytics scripts reading location.href). The SDK posts
// HANDSHAKE_INIT to the iframe on frame `load` (transferring one MessagePort),
// the iframe replies HANDSHAKE_ACK on that port, and the SDK then delivers
// credentials in a CONFIGURE message over the same private port.
//
// This module lives in @highnoteplatform/types — not @highnoteplatform/iframes —
// deliberately: the published SDKs' parcel builds bundle ONLY this package
// (targets.*.includeNodeModules allowlist, built with --no-scope-hoist, i.e. no
// tree-shaking), so importing the iframes package here would drag its entire
// re-export graph into the public SDK bundles. Do not "tidy" this into iframes.

export const SECURE_CHANNEL_HANDSHAKE_INIT = "SECURE_CHANNEL_HANDSHAKE_INIT";
export const SECURE_CHANNEL_HANDSHAKE_ACK = "SECURE_CHANNEL_HANDSHAKE_ACK";
export const SECURE_CHANNEL_CONFIGURE = "SECURE_CHANNEL_CONFIGURE";

export const HANDSHAKE_TIMEOUT_MS = 10_000;
export const CONFIGURE_TIMEOUT_MS = 15_000;

// Sent SDK -> iframe via window.postMessage, carrying the MessagePort.
export type SecureChannelHandshakeInitMessage = Readonly<{
  __typename: typeof SECURE_CHANNEL_HANDSHAKE_INIT;
  uuid: string;
}>;

// Sent iframe -> SDK over the transferred MessagePort.
export type SecureChannelHandshakeAckMessage = Readonly<{
  __typename: typeof SECURE_CHANNEL_HANDSHAKE_ACK;
}>;

// Sent SDK -> iframe over the MessagePort after the ACK. paymentCardId is
// omitted by flows that have no card id (e.g. secure-inputs tokenization).
export type SecureChannelConfigureMessage = Readonly<{
  __typename: typeof SECURE_CHANNEL_CONFIGURE;
  clientToken: string;
  paymentCardId?: string;
  // Descriptive SDK identity for adoption telemetry. Optional and additive —
  // absent for older parents. Never authoritative.
  sdkName?: string;
  sdkVersion?: string;
}>;

// Why the SDK-side handshake failed. The consuming SDK maps these to its own
// public error type and user-facing wording.
export type SecureChannelFailure = "FRAME_UNREACHABLE" | "HANDSHAKE_TIMEOUT";

export const hasTypename = (data: unknown, typename: string): boolean =>
  typeof data === "object" &&
  data !== null &&
  "__typename" in data &&
  (data as { __typename: unknown }).__typename === typename;

interface EstablishSecureChannelConfig {
  frame: HTMLIFrameElement;
  iframeOrigin: string;
  uuid: string;
  clientToken: string;
  paymentCardId?: string;
  sdkName?: string;
  sdkVersion?: string;
  onFailure: (failure: SecureChannelFailure) => void;
  // Invoked when the parent receives HANDSHAKE_ACK — the point after which the
  // iframe is the sole authority for handshake failure. Optional and additive.
  onHandshakeAck?: () => void;
  timeoutMs?: number;
}

// Delivers credentials to a hosted iframe over a private MessagePort so they
// never appear in the iframe URL. The init message is posted on iframe
// `load`; the iframe registers its listener during script evaluation, which
// always precedes `load`.
//
// Returns a disposer that detaches the load listener and tears down any
// pending channel — callers MUST invoke it on unmount so no timer can fire
// `onFailure` after the fields are gone.
export const establishSecureChannel = ({
  frame,
  iframeOrigin,
  uuid,
  clientToken,
  paymentCardId,
  sdkName,
  sdkVersion,
  onFailure,
  onHandshakeAck,
  timeoutMs = HANDSHAKE_TIMEOUT_MS,
}: Readonly<EstablishSecureChannelConfig>): (() => void) => {
  let disposed = false;
  let failed = false;
  let cleanupPending: (() => void) | undefined;

  // onFailure is one-shot for the channel's lifetime. A frame can emit `load`
  // more than once (reload), and both the FRAME_UNREACHABLE and
  // HANDSHAKE_TIMEOUT paths can be reached again on a subsequent load — the
  // integrator must not be re-notified of the same failed handshake, nor
  // receive a failure after an earlier one already fired.
  const reportFailure = (failure: SecureChannelFailure) => {
    if (failed) {
      return;
    }

    failed = true;
    onFailure(failure);
  };

  const onLoad = () => {
    // A reloaded frame is a fresh document with a fresh listener; tear down
    // the previous channel so its timeout cannot fire a spurious error.
    cleanupPending?.();
    cleanupPending = undefined;

    const targetWindow = frame.contentWindow;

    if (targetWindow === null) {
      reportFailure("FRAME_UNREACHABLE");

      return;
    }

    const channel = new MessageChannel();
    const sdkPort = channel.port1;

    const timeout = setTimeout(() => {
      sdkPort.close();
      cleanupPending = undefined;

      reportFailure("HANDSHAKE_TIMEOUT");
    }, timeoutMs);

    cleanupPending = () => {
      clearTimeout(timeout);
      sdkPort.close();
    };

    sdkPort.onmessage = (event: MessageEvent) => {
      if (!hasTypename(event.data, SECURE_CHANNEL_HANDSHAKE_ACK)) {
        return;
      }

      clearTimeout(timeout);
      onHandshakeAck?.();

      const configure: SecureChannelConfigureMessage = {
        __typename: SECURE_CHANNEL_CONFIGURE,
        clientToken,
        ...(paymentCardId === undefined ? {} : { paymentCardId }),
        ...(sdkName === undefined ? {} : { sdkName }),
        ...(sdkVersion === undefined ? {} : { sdkVersion }),
      };

      sdkPort.postMessage(configure);
      // CONFIGURE is the last message on this channel; the iframe closes its
      // end on receipt. Close ours too for lifecycle symmetry — the message
      // is already queued on the peer, so closing here does not drop it.
      // (cleanupPending is left as-is; the disposer's close() is idempotent.)
      sdkPort.close();
    };

    const init: SecureChannelHandshakeInitMessage = {
      __typename: SECURE_CHANNEL_HANDSHAKE_INIT,
      uuid,
    };

    targetWindow.postMessage(init, iframeOrigin, [channel.port2]);
  };

  frame.addEventListener("load", onLoad);

  return () => {
    if (disposed) {
      return;
    }

    disposed = true;
    frame.removeEventListener("load", onLoad);
    cleanupPending?.();
    cleanupPending = undefined;
  };
};

// One failed handshake must surface at most once to the integrator, and never
// zero times. Both the parent timeout (FRAME_UNREACHABLE / HANDSHAKE_TIMEOUT)
// and the iframe's relayed configure-timeout converge on the same integrator
// callback; this gate deduplicates them at that convergence point. The dedup
// window closes on ACK receipt: every genuine, must-surface error is
// credential-dependent (it needs CONFIGURE, which the parent sends only after
// the ACK), so ACK strictly precedes it. Before ACK, only handshake-phase
// failures can fire, and they are safe to dedupe; after ACK the iframe is the
// sole authority and every relayed error passes through untouched.
//
// One gate per render. Two known limitations remain — both never-silent
// (>=1 callback) and deliberately accepted (DX-8274):
//   1. A non-credentialed sibling frame (e.g. cvv/expiry) that relays a
//      parse/style error BEFORE ACK is collapsed into a concurrent handshake
//      failure. Rare; out of scope.
//   2. The gate lives for the whole render, but establishSecureChannel re-arms
//      both clocks on every `load`, so a success -> reload -> failed-handshake
//      sequence surfaces the double again. A per-load epoch reset would close
//      it; deferred as a follow-up.
export interface HandshakeGate {
  // Call when the parent receives HANDSHAKE_ACK (wire to establishSecureChannel's
  // onHandshakeAck).
  ack: () => void;
  // Wraps the integrator callback for the parent-timeout path. Pre-ACK in the
  // common single-load flow; not guaranteed pre-ACK across reloads (limitation 2).
  wrapFailure: <E>(deliver: (e: E) => void) => (e: E) => void;
  // Wraps the integrator callback for the iframe-relay path (handlePostMessage).
  wrapRelay: <E>(deliver: (e: E) => void) => (e: E) => void;
}

export const createHandshakeGate = (): HandshakeGate => {
  let settled = false; // one handshake-phase error already surfaced
  let configurePhase = false; // ACK received -> iframe is sole authority

  return {
    ack: () => {
      configurePhase = true;
    },
    wrapFailure:
      <E>(deliver: (e: E) => void) =>
      (e: E) => {
        if (settled) {
          return;
        }

        settled = true;
        deliver(e);
      },
    wrapRelay:
      <E>(deliver: (e: E) => void) =>
      (e: E) => {
        if (configurePhase) {
          deliver(e); // post-ACK: pass every relayed error unconditionally

          return;
        }

        if (settled) {
          return; // pre-ACK duplicate: swallow
        }

        settled = true;
        deliver(e);
      },
  };
};
